import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// The company directory. Refreshed on mount rather than cached hard: the
// underlying people table rebuilds nightly and the Ops sheet syncs hourly, so
// a long staleTime would show yesterday's leaver as current staff.
export function useDirectory() {
  return useQuery({
    queryKey: ['directory'],
    queryFn: async () => {
      const { data } = await api.get('/api/directory');
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

// Submitting a suggestion. Not optimistic and not cached: nothing on the
// directory changes as a result — the value only moves once the owning team
// makes the change at source — so there is no local state to update.
export function useSuggestEdit() {
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await api.post('/api/directory/suggestions', payload);
      return data;
    },
  });
}

// The review queue. Full-admin only server-side; a 403 simply means the panel
// isn't shown, so no client-side role check is needed to keep it safe.
export function useSuggestions() {
  return useQuery({
    queryKey: ['directory', 'suggestions'],
    queryFn: async () => {
      const { data } = await api.get('/api/directory/suggestions');
      return data;
    },
    retry: false,
    staleTime: 30 * 1000,
  });
}

export function useReviewSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, decision, note }) => {
      const { data } = await api.post(`/api/directory/suggestions/${id}/review`, { decision, note });
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['directory', 'suggestions'] }),
  });
}

// Marks an approved correction as actually done in the owning system. With no
// automatic notification to HR/Ops/IT, this is what stops the queue quietly
// filling with approved-but-never-applied items.
export function useMarkApplied() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { data } = await api.post(`/api/directory/suggestions/${id}/applied`);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['directory', 'suggestions'] }),
  });
}
